import { useContext, useState, useEffect } from 'react';
import { SmartContext } from '../../../../../../library/Core/SmartContext';
import ProfileMakerService from './ProfileMakerService';
import { Tooltip } from 'bootstrap';
import { allowPerformAction, displayActionNotAllowedAlert, getActionElementClass, isEmpty, setError, getFlexColDomainValueForCode } from '../../../../../../library/Core/SmartFunctions';
import { toast } from 'react-toastify';
import EditWorkProfileModal from './EditWorkProfileModal';
import WorkProfileConfig from './FormConfiguration/WorkProfileConfig.json';

type WorkProfileProperties = {
    is_leadership_role_in_large_company: { label: string; toolTip: string; isComment?: string };
    is_leadership_role_in_500_company: { label: string; toolTip: string; isComment?: string };
    work_experience: { label: string; toolTip: string; isComment?: string };
    is_leadership_role_in_a_listed_company: { label: string; toolTip: string; isComment?: string };
    is_leadership_role_in_sme_company: { label: string; toolTip: string; isComment?: string };
    is_growing_startup_founder: { label: string; toolTip: string; isComment?: string };
    is_mid_senior_role_in_fortune_company: { label: string; toolTip: string; isComment?: string };
    director_or_board_member: { label: string; toolTip: string; isComment?: string };
    runs_a_business: { label: string; toolTip: string; isComment?: string };
    seniority_level: { label: string; toolTip: string; isComment?: string };
    working_in_education_company: { label: string; toolTip: string; isComment?: string };
};

const WorkProfileProperties: WorkProfileProperties = {
    is_leadership_role_in_large_company: {
        label: 'Leadership Role in Large Company',
        toolTip: 'Alumnus is in "Leadership" roles like "CEO, COO, SVP, VP, National Head" etc in a company with 1000+ employees',
        isComment: 'is_leadership_role_in_large_companyComment',
    },
    is_leadership_role_in_500_company: {
        label: 'Leadership Role in Fortune 500 Company',
        toolTip: 'Alumnus is in "Leadership" roles like "CEO, COO, SVP, VP, National Head" etc in a Fortune 500 company',
        isComment: 'is_leadership_role_in_500_companyComment',
    },
    work_experience: {
        label: 'Work Experience > 15 Years',
        toolTip: 'Alumnus has overall work experience of more than 15 years',
        isComment: 'work_experienceComment',
    },
    is_leadership_role_in_a_listed_company: {
        label: 'Leadership Role in a Listed Company',
        toolTip: 'Alumnus is in Leadership roles like CEO, COO, SVP, VP, Director etc in a company that is listed on Stock Exchanges like BSE, NSE, NYSE, LSE etc',
        isComment: 'is_leadership_role_in_a_listed_companyComment',
    },
    is_leadership_role_in_sme_company: {
        label: 'Leadership Role in SME Company',
        toolTip: 'Alumnus is in "Leadership" roles like "CEO, COO, SVP, VP, National Head" etc in a company with 200-1000 employees',
        isComment: 'is_leadership_role_in_sme_companyComment',
    },
    is_growing_startup_founder: {
        label: 'Growing Start-up Founder',
        toolTip: 'Alumnus is a "Founder/ Co-Founder" or a unicorn startup or a startup with 250+ employees',
        isComment: 'is_growing_startup_founderComment',
    },
    is_mid_senior_role_in_fortune_company: {
        label: 'Mid-Senior Role in Fortune 500 Company',
        toolTip: 'Alumnus is in "Mid-Senior" roles like "Director, Regional Manager, Senior Manager" etc in a Fortune 500 company',
        isComment: 'is_mid_senior_role_in_fortune_companyComment',
    },
    director_or_board_member: {
        label: 'Director OR Board Member of Company',
        toolTip: 'Alumnus is a "Director" or "Board Member" of any company as per MCA India, or similar international entities',
        isComment: 'director_or_board_memberComment',
    },
    runs_a_business: {
        label: 'Runs a Business',
        toolTip: 'Family Business / Own Started Business',
        isComment: 'runs_a_businessComment',
    },
    seniority_level: {
        label: 'Seniority Level',
        toolTip: 'Senior Mgmt, Mid Mgmt, Manager, Others',
        isComment: 'seniority_levelComment',
    },
    working_in_education_company: {
        label: 'Working in Education Company',
        toolTip: 'Alumni employed in Education sector',
        isComment: 'working_in_education_companyComment',
    },
};

const keys = Object.keys(WorkProfileProperties) as (keyof WorkProfileProperties)[];

const WorkProfileNew = (props: any) => {
    const { state, dispatch } = useContext(SmartContext);
    const [isOpen, setIsOpen] = useState<Record<string, boolean>>({});
    const markers = state.data.marker;
    const workProfileMarkers = markers?.workProfile ?? {};
    const [loading, setLoading] = useState(true);
    const { saveWorkRelatedMarker, saveSuggestionResponse, fetchUserProfileMarkers, fetchUserSyncDetails, handleOpenLogsModal } = ProfileMakerService();
    const [isConfigAvailable, setisConfigAvailable] = useState(false);
    const model = state.internal.model;

    const handleOpenModal = async (key: string) => {
        if (!allowPerformAction()) {
            displayActionNotAllowedAlert();
            return;
        }
        dispatch({ type: 'RESET_VALIDATION_ERRORS' });

        const marker = getMarkerForKey(key);

        const prefillData = marker ? {
            [key]: marker.markerEnabled,
            [`${key}_value`]: marker.value,
            [`${key}Comment`]: marker.remark ?? '',
        } : {
            [key]: 0,
            [`${key}_value`]: '',
            [`${key}Comment`]: '',
        };

        dispatch({
            type: 'REFRESH_DATA',
            payload: {
                data: {
                    ...state.data,
                    visibleControl: key,
                    workProfile: {
                        ...state.data.workProfile,
                        ...prefillData,
                    },
                },
            },
        });

        dispatch({ type: 'TOGGLE_MODAL_VISIBILITY', payload: { modalName: 'showWorkProfileModal' } });
    };

    const getRemarkForKey = (key: string) => {
        const numericMarkers = Object.entries(workProfileMarkers)
            .filter(([k]) => /^\d+$/.test(k))
            .map(([, v]) => v as any);
        const marker = numericMarkers.find((m: any) => m.markerField === key);
        return marker?.remark || null;
    };

    const getMarkerForKey = (key: string) => {
        const numericMarkers = Object.entries(workProfileMarkers)
            .filter(([k]) => /^\d+$/.test(k))
            .map(([, v]) => v as any);
        return numericMarkers.find((m: any) => m.markerField === key);
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const existingMarker = state.data.marker;
                const userSyncdata = await fetchUserSyncDetails(state?.internal?.model?.id);
                const domainMap = new Map([...state.domain]);
                const config = await Promise.resolve(WorkProfileConfig);

                dispatch({
                    type: 'CONTROL_VALUE_CHANGE',
                    payload: { dataKey: 'syncData', value: userSyncdata },
                });

                dispatch({
                    type: 'DATA_INIT',
                    payload: {
                        formConfig: config,
                        data: {
                            ...state.data,
                            workProfile: { ...existingMarker?.workProfile },
                            visibleControl: {},
                        },
                        domain: domainMap,
                        internal: state.internal,
                        routeInfo: state.routeInfo,
                    },
                });

                setisConfigAvailable(true);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching marker sync data:', error);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        var tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
        var tooltipList = tooltipTriggerList.map(function (tooltipTriggerEl) {
            return new Tooltip(tooltipTriggerEl);
        });
    }, [markers]);

    const handleAccept = async (key: string, value: string, justification?: string) => {
        if (!allowPerformAction()) return;

        const marker = getMarkerForKey(key);
        if (!marker) {
            console.warn("No marker found for key:", key);
            return;
        }

        const updatedMarker = {
            ...marker,
            markerEnabled: value === "yes" ? 1 : 0,
            remark: justification || '',
            value: key === "work_experience" ? (value === "yes" ? "15" : "0") : '',
        };

        const suggestionKey = key === "work_experience" ? "work_ex_more_than_15_years" : key;

        await saveWorkRelatedMarker(updatedMarker, state.internal.model.id);
        await saveSuggestionResponse(suggestionKey, state.internal.model.id, "Accepted");
        await fetchUserProfileMarkers(state.internal.model.id);
        toast.success("Suggestion Accepted");
    };

    const handleReject = async (key: string) => {
        if (!allowPerformAction()) return;
        const suggestionKey = key == 'work_experience' ? 'work_ex_more_than_15_years' : key;
        await saveSuggestionResponse(suggestionKey, state.internal.model.id, 'Rejected');
        await fetchUserProfileMarkers(state?.internal?.model?.id);
        toast.success("Suggestion Rejected");
    };

    return (
        <>
            <div className="tab-pane fade active show" id="v-pills-work-profile" role="tabpanel" aria-labelledby="v-pills-work-profile-tab">
                <div className="max-470 mx-auto">
                    <div className="row g-3">
                        {keys.map((key) => {
                            const marker = getMarkerForKey(key);
                            const markerExists = marker ? (key === 'work_experience' ? parseFloat(marker.value) >= 15 : marker.markerEnabled === 1) : false;
                            const remark = getRemarkForKey(key);

                            return (
                                <div className="col-12" key={key}>
                                    <div className="row g-3 align-items-center">
                                        <div className="col position-relative">
                                            {(() => {
                                                const analysis = state?.data?.marker?.workProfile?.[`${key}Analysis`];
                                                const isPendingAnalysis = analysis && !analysis.status;
                                                const shouldShowSuggestion = isPendingAnalysis;

                                                return (
                                                    <>
                                                        {/** ---------- TOGGLE ARROW BUTTON ---------- */}
                                                        {shouldShowSuggestion && (
                                                            <button
                                                                className="icon-down-arrow border-0 position-absolute bg-transparent btn-profile-toggle z-1"
                                                                type="button"
                                                                data-bs-target={`#${key}Card`}
                                                                aria-expanded={isOpen?.[key]}
                                                                onClick={() => setIsOpen((prev: any) => ({ ...prev, [key]: !prev[key] }))}
                                                            ></button>
                                                        )}

                                                        {/** ---------- MAIN LIST ITEM ---------- */}
                                                        <div className={`listed-border ${markerExists ? "" : "disabled"}`}>
                                                            <i className="tick"></i>
                                                            <p
                                                                onClick={() => !loading && handleOpenModal(key)}
                                                                style={{ cursor: loading ? 'not-allowed' : 'pointer' }}
                                                            >
                                                                {WorkProfileProperties[key].label}
                                                            </p>

                                                            {/* balanceshets */}
                                                            {(key === 'is_leadership_role_in_sme_company' ||
                                                                    key === 'is_growing_startup_founder') &&
                                                                    (() => {
                                                                        const companyId = marker?.value;
                                                                        if (!companyId || isNaN(Number(companyId))) {
                                                                            return null;
                                                                        }
                                                                        const flexCol = getFlexColDomainValueForCode(
                                                                            companyId,
                                                                            'COMPANY_CODE',
                                                                            state
                                                                        );
                                                                        console.log("flexCol", flexCol);
                                                                        let balanceSheetPath: string | null = null;
                                                                        if (!isEmpty(flexCol)) {
                                                                            try {
                                                                                const parsed =
                                                                                    typeof flexCol === 'string'
                                                                                        ? JSON.parse(flexCol)
                                                                                        : flexCol;

                                                                                balanceSheetPath =
                                                                                    parsed?.balance_sheets || null
                                                                            } catch (e) {
                                                                                balanceSheetPath = null;
                                                                            }
                                                                        }

                                                                        return balanceSheetPath ? (
                                                                        <a
                                                                            className="text-white me-2"
                                                                            href={balanceSheetPath}
                                                                            target="_blank"
                                                                            data-bs-toggle="tooltip"
                                                                            data-bs-html="true"
                                                                            title="Balance Sheet"
                                                                            onClick={(event) => event.stopPropagation()}
                                                                        ><i className="icon-edit"></i></a> 
                                                                        ) : null;
                                                                    })()}
                                                            {/* end balance sheets */}

                                                            {markerExists && remark && (
                                                                <span
                                                                    className="note-wrap"
                                                                    data-bs-toggle="tooltip"
                                                                    data-bs-html="true"
                                                                    title={remark}
                                                                >
                                                                    <i className="icon-note"></i>
                                                                </span>
                                                            )}
                                                        </div>

                                                        {/** ---------- COLLAPSIBLE ANALYSIS BODY ---------- */}
                                                        {shouldShowSuggestion && (
                                                            <div
                                                                className={`listed-body ${analysis.confidence == 'high' ? 'orange' : 'yellow'} p-3 collapse ${isOpen?.[key] == true ? 'show' : ''}`}
                                                                id={`${key}Card`}
                                                            >
                                                                <p>Value - {analysis.marker_value[0]?.toUpperCase() + analysis.marker_value.slice(1)}</p>
                                                                <p>{analysis.justification}</p>

                                                                <div className="d-flex align-items-center">
                                                                    <button
                                                                        className="btn-green height-30 me-2"
                                                                        onClick={() => handleAccept(key, analysis.marker_value, analysis.justification)}
                                                                    >
                                                                        <span>Accept</span>
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-danger rounded-pill height-30 lh-base d-flex align-items-center"
                                                                        onClick={() => handleReject(key)}
                                                                    >
                                                                        <span>Reject</span>
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </div>

                                        <div className="col-auto">
                                            <i
                                                className="icon-info"
                                                data-bs-toggle="tooltip"
                                                title=""
                                                data-bs-original-title={WorkProfileProperties[key].toolTip}
                                                aria-label={WorkProfileProperties[key].toolTip}
                                            ></i>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        <div className="col-12 pt-3">
                            <button
                                className="btn btn-dark-blue w-auto height-40 px-4 d-flex align-items-center mx-auto"
                                onClick={() => handleOpenLogsModal()}
                            >
                                <span className="text-white">View Update Logs</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {isConfigAvailable && <EditWorkProfileModal userdata={{ model }} workProfileProperties={WorkProfileProperties} />}
        </>
    );
};

export default WorkProfileNew;
